import { useState, useEffect } from 'react';
import { X, Loader2, Pencil, Trash2, Unlink } from 'lucide-react';
import { API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import type { Resource, ResourceType, ResourceStatus } from '../../types';

const typeOptions: ResourceType[] = ['vehicle', 'equipment', 'personnel', 'facility'];
const statusOptions: ResourceStatus[] = ['available', 'deployed', 'en_route', 'maintenance', 'offline', 'reserved'];
const departmentOptions = ['Fire', 'Police', 'EMS', 'Hazmat', 'Search & Rescue', 'Other'];

interface EditResourceModalProps {
  open: boolean;
  onClose: () => void;
  resource: Resource | null;
  onSaved?: (resource: Resource) => void;
  onDeleted?: (resourceId: string) => void;
}

export default function EditResourceModal({ open, onClose, resource, onSaved, onDeleted }: EditResourceModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [name, setName] = useState('');
  const [type, setType] = useState<ResourceType>('vehicle');
  const [status, setStatus] = useState<ResourceStatus>('available');
  const [department, setDepartment] = useState('Fire');
  const [capabilities, setCapabilities] = useState('');
  const [assignedPersonnel, setAssignedPersonnel] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!resource) return;
    setName(resource.name || '');
    setType(resource.type || 'vehicle');
    setStatus(resource.status || 'available');
    setDepartment(resource.department || 'Fire');
    setCapabilities((resource.capabilities || []).join(', '));
    setAssignedPersonnel((resource.assignedPersonnel || []).join(', '));
    setNotes(resource.notes || '');
    setConfirmDelete(false);
  }, [resource]);

  if (!open || !resource) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const capsList = capabilities.split(',').map((c) => c.trim()).filter(Boolean);
      const personnelList = assignedPersonnel.split(',').map((p) => p.trim()).filter(Boolean);

      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: name.trim(),
          type,
          status,
          department,
          capabilities: capsList,
          assignedPersonnel: personnelList,
          notes: notes.trim() || undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        onSaved?.(data.resource);
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        onDeleted?.(resource.id);
        onClose();
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDisconnectMDT = async () => {
    setDisconnecting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}/disconnect-mdt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        onSaved?.(data.resource);
        onClose();
      }
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Pencil className="w-4 h-4 text-primary" />
            Edit Resource <span className="text-muted-foreground font-mono text-xs">({resource.id})</span>
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Call Sign / Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="e.g. MD01, Engine-7"
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as ResourceType)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ResourceStatus)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Assigned Personnel</label>
            <input
              value={assignedPersonnel}
              onChange={(e) => setAssignedPersonnel(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Comma separated, e.g. Rachel Brooks, John Smith"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">Names of persons currently assigned to this resource</p>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Capabilities</label>
            <input
              value={capabilities}
              onChange={(e) => setCapabilities(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Comma separated, e.g. pumper, rescue, aerial"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              placeholder="Optional notes..."
            />
          </div>
          {resource.inUseBy && (
            <div className="flex items-center gap-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-xs text-orange-400 font-medium">MDT Connected</span>
              <button
                type="button"
                onClick={handleDisconnectMDT}
                disabled={disconnecting}
                className="ml-auto px-2 py-1 rounded bg-orange-500 text-white text-xs font-medium hover:bg-orange-600 transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                {disconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
                Disconnect MDT
              </button>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors flex items-center gap-1.5 ${
                confirmDelete
                  ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                  : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
              }`}
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
